import Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  CLIENT_MESSAGES,
  SERVER_MESSAGES,
  type AddBotMessage,
  type ChatMessage,
  type KickPlayerMessage,
  type RemoveSeatMessage,
  type RoomOptions,
  type RoomSeatRow,
  type ServerChatMessage,
  type ServerGameLogMessage,
  type ServerRoomStateMessage,
  type UpdateRoomOptionsMessage,
} from '@dalmuti/shared';
import { resetOverlay } from '../ui/overlay.js';
import { mountBackdrop } from '../ui/backdrop.js';
import { transitionTo } from '../ui/transition.js';

interface RoomSceneData {
  room: Room;
}

interface LogLine {
  text: string;
  ts: number;
}

export class RoomScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;
  private room: Room | null = null;
  private state: ServerRoomStateMessage | null = null;
  private chat: { fromName: string; text: string; ts: number }[] = [];
  private log: LogLine[] = [];
  private transitioned = false;

  constructor() {
    super('Room');
  }

  init(data: RoomSceneData): void {
    this.room = data.room;
    // Phaser reuses this scene instance across visits, so instance fields
    // survive from a previous room. The killer here is `transitioned`: left
    // `true` from a prior game's GameScene handoff, the ROOM_STATE handler
    // bails at its top guard and never renders seats — the seat grid stays
    // empty. `log`/`chat`/`state` carry stale content from the old room too.
    this.transitioned = false;
    this.state = null;
    this.chat = [];
    this.log = [];
  }

  create(): void {
    resetOverlay();

    // Backdrop (stays matched to the viewport across rotations).
    mountBackdrop(this, { color: 0x2c0b0e, depth: -10 });

    this.mountUI();
    this.bindRoom();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unmountUI());
  }

  private bindRoom(): void {
    const room = this.room;
    if (!room) {
      this.goTo('Lobby');
      return;
    }
    room.onMessage(
      SERVER_MESSAGES.ROOM_STATE,
      (msg: ServerRoomStateMessage) => {
        // Stale Colyseus handlers survive after Phaser stops this scene,
        // so re-firing this would cause a transition loop.
        if (this.transitioned) return;
        console.log('[RoomScene] ROOM_STATE phase=', msg.phase, 'seats=', msg.seatOrder.length, 'specs=', msg.spectators.length);
        this.state = msg;
        this.render();
        // Fires the options-modal reflow if it's open.
        this.events.emit('room-state');
        // Only the active game phases should push us into GameScene.
        // 'finished' should fall back to the lobby instead.
        const gamePhases = [
          'dealing',
          'revolution',
          'tax',
          'playing',
          'round_end',
        ];
        if (gamePhases.includes(msg.phase)) {
          console.log('[RoomScene] phase triggers GameScene transition');
          this.transitioned = true;
          this.goTo('Game', { room });
        }
      },
    );
    room.onMessage(SERVER_MESSAGES.CHAT, (msg: ServerChatMessage) => {
      this.chat.push({
        fromName: msg.fromName,
        text: msg.text,
        ts: msg.ts,
      });
      if (this.chat.length > 100) this.chat.shift();
      this.renderChat();
    });
    room.onMessage(SERVER_MESSAGES.GAME_LOG, (msg: ServerGameLogMessage) => {
      this.log.push(msg);
      if (this.log.length > 50) this.log.shift();
      this.renderLog();
    });
    room.onMessage(
      SERVER_MESSAGES.ERROR,
      (msg: { code: string; text: string }) => {
        this.setStatus(msg.text || msg.code);
      },
    );
    room.onLeave(() => this.goTo('Lobby'));
  }

  private goTo(key: string, data?: object): void {
    this.unmountUI();
    transitionTo(this, key, data);
  }

  private mountUI(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const wrap = document.createElement('div');
    wrap.style.cssText = `
      position: absolute;
      inset: 0;
      display: grid;
      grid-template-rows: 64px 1fr;
      pointer-events: auto;
      box-sizing: border-box;
    `;
    wrap.innerHTML = `
      <style>
        /* Waiting-room responsive — matches the lobby. Below 760px the seats
           are the full-width main view; chat + log collapse into a bottom-sheet
           drawer opened by a floating button. */
        #dm-room-fabs { display: none; }
        #dm-room-backdrop {
          display: none; position: fixed; inset: 0;
          background: rgba(8,3,5,0.6); z-index: 140;
        }
        .dm-drawer-handle { display: none; }
        #dm-seat-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
        /* Narrow OR short (landscape phone) → compact, scrolling, chat-as-drawer. */
        @media (max-width: 760px), (max-height: 540px) {
          header { padding: 0 12px !important; gap: 8px !important; }
          #dm-room-title { font-size: 15px !important; }
          #dm-invite span { display: none; }
          /* Single column that scrolls vertically — the seat panel grows with
             its content instead of overflowing the viewport. Seats auto-fill so
             a wide landscape phone shows several columns. */
          #dm-room-main {
            display: block !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
            padding: 10px 10px 92px !important;
          }
          .dm-seats-panel { height: auto !important; min-height: 0 !important; }
          #dm-seat-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important; }
          .dm-chat-panel {
            position: fixed; left: 0; right: 0; bottom: var(--kb-inset, 0px);
            height: 74vh; max-height: calc(86vh - var(--kb-inset, 0px));
            margin: 0; z-index: 150;
            border-radius: 18px 18px 0 0;
            transform: translateY(106%); transition: transform 0.28s ease;
          }
          .dm-chat-panel.dm-open { transform: translateY(0); }
          .dm-drawer-handle { display: flex; }
          #dm-room-fabs { display: flex; }
          #dm-room-backdrop.dm-show { display: block; }
        }
      </style>

      <header style="display:flex; align-items:center; padding:0 24px; background: linear-gradient(180deg, rgba(245,200,105,0.08) 0%, rgba(0,0,0,0.0) 100%); border-bottom: 1px solid rgba(245,200,105,0.25); gap:14px;">
        <button id="dm-leave" class="lux-btn-ghost" style="padding:6px 14px; flex-shrink:0;">← 로비</button>
        <div style="min-width:0; flex:1;">
          <div id="dm-room-title" style="font-size:18px; font-weight:800; color:#f5c869; letter-spacing:0.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">방</div>
          <div id="dm-room-meta" style="font-size:11px; color:rgba(244,236,214,0.6); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;"></div>
        </div>
        <button id="dm-invite" class="lux-btn-ghost" style="padding:8px 14px; flex-shrink:0;">🔗<span> 초대 링크</span></button>
      </header>

      <div id="dm-room-main" style="display:grid; grid-template-columns: 1fr 340px; gap:18px; padding:18px; min-height:0; box-sizing:border-box;">
        <section class="lux-panel dm-seats-panel">
          <h3>자리</h3>
          <div class="lux-divider"></div>
          <div id="dm-seat-grid" style="display:grid; gap:10px;"></div>

          <div id="dm-host-controls" style="display:none; margin-top:18px; padding:14px; background:rgba(245,200,105,0.06); border:1px solid rgba(245,200,105,0.25); border-radius:10px;">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; gap:8px; flex-wrap:wrap;">
              <div style="font-weight:700; color:#f5c869; letter-spacing:0.5px;">👑 방장 권한</div>
              <button id="dm-open-options" class="lux-btn-ghost" style="font-size:12px; padding:5px 12px;">⚙ 옵션 설정</button>
            </div>
            <div style="display:flex; gap:8px; flex-wrap:wrap;">
              <button id="dm-add-bot" class="lux-btn-ghost" style="flex:1 1 120px; padding:10px;">＋ 봇 추가</button>
              <button id="dm-start" class="lux-btn-success" style="flex:2 1 160px; padding:10px; font-size:14px;">게임 시작</button>
            </div>
          </div>
          <div id="dm-player-controls" style="display:none; margin-top:18px; text-align:center;">
            <button id="dm-ready" class="lux-btn-primary" style="padding:12px 36px; font-size:15px;">레디</button>
          </div>
          <div id="dm-status" style="font-size:12px; color:#ffb878; min-height:14px; text-align:center; margin-top:10px;"></div>
        </section>

        <section class="lux-panel dm-chat-panel">
          <div class="dm-drawer-handle" style="justify-content:space-between; align-items:center; margin-bottom:4px;">
            <span style="font-size:11px; color:rgba(244,236,214,0.5);">아래로 닫기</span>
            <button class="dm-drawer-close lux-btn-ghost" style="padding:3px 12px; font-size:13px;">✕</button>
          </div>
          <h3>방 채팅</h3>
          <div class="lux-divider"></div>
          <div id="dm-chat-log" class="lux-scroll" style="overflow-y:auto; flex:1; min-height:140px; display:flex; flex-direction:column; gap:4px; font-size:13px; padding:10px; background:rgba(0,0,0,0.25); border-radius:8px; border:1px solid rgba(245,200,105,0.12);"></div>
          <div style="display:flex; gap:6px; margin-top:8px;">
            <input id="dm-chat-input" class="lux-input" type="text" maxlength="200" enterkeyhint="send" autocomplete="off" placeholder="메시지를 입력하세요..." style="flex:1; padding:8px 10px; font-size:13px;">
            <button id="dm-chat-send" class="lux-btn-primary" style="padding:8px 14px;">전송</button>
          </div>
          <h4 style="margin-top:14px;">로그</h4>
          <div id="dm-log" class="lux-scroll" style="overflow-y:auto; max-height:140px; font-size:11px; color:rgba(244,236,214,0.65); padding:8px; background:rgba(0,0,0,0.25); border-radius:8px; border:1px solid rgba(245,200,105,0.12);"></div>
        </section>
      </div>

      <!-- Mobile drawer backdrop + floating chat button -->
      <div id="dm-room-backdrop"></div>
      <div id="dm-room-fabs" style="position:fixed; bottom:16px; left:50%; transform:translateX(-50%); gap:12px; z-index:135; pointer-events:auto;">
        <button id="dm-fab-chat" class="lux-btn-primary" style="padding:12px 22px; font-size:14px; border-radius:24px; box-shadow:0 6px 16px rgba(0,0,0,0.5);">💬 채팅 / 로그</button>
      </div>
    `;
    root.appendChild(wrap);
    this.overlay = wrap;

    // Mobile chat drawer: the floating button opens the chat/log bottom sheet.
    const chatPanel = wrap.querySelector<HTMLElement>('.dm-chat-panel');
    const backdrop = wrap.querySelector<HTMLElement>('#dm-room-backdrop');
    const closeDrawer = (): void => {
      chatPanel?.classList.remove('dm-open');
      backdrop?.classList.remove('dm-show');
    };
    wrap
      .querySelector<HTMLButtonElement>('#dm-fab-chat')
      ?.addEventListener('click', () => {
        chatPanel?.classList.add('dm-open');
        backdrop?.classList.add('dm-show');
      });
    backdrop?.addEventListener('click', closeDrawer);
    wrap
      .querySelectorAll<HTMLButtonElement>('.dm-drawer-close')
      .forEach((b) => b.addEventListener('click', closeDrawer));

    wrap
      .querySelector<HTMLButtonElement>('#dm-leave')!
      .addEventListener('click', () => {
        this.room?.leave().catch(() => {});
      });
    wrap
      .querySelector<HTMLButtonElement>('#dm-invite')!
      .addEventListener('click', () => this.copyInviteLink());
    wrap
      .querySelector<HTMLButtonElement>('#dm-add-bot')!
      .addEventListener('click', () => {
        const payload: AddBotMessage = {};
        this.room?.send(CLIENT_MESSAGES.ADD_BOT, payload);
      });
    wrap
      .querySelector<HTMLButtonElement>('#dm-start')!
      .addEventListener('click', () => {
        this.room?.send(CLIENT_MESSAGES.START_GAME, {});
      });
    wrap
      .querySelector<HTMLButtonElement>('#dm-ready')!
      .addEventListener('click', () => {
        this.room?.send(CLIENT_MESSAGES.READY, {});
      });
    wrap
      .querySelector<HTMLButtonElement>('#dm-open-options')!
      .addEventListener('click', () => this.openOptionsModal());

    const grid = wrap.querySelector<HTMLDivElement>('#dm-seat-grid')!;
    grid.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.classList.contains('dm-remove-bot')) {
        const payload: RemoveSeatMessage = { seat: Number(t.dataset.seat) };
        this.room?.send(CLIENT_MESSAGES.REMOVE_SEAT, payload);
      } else if (t.classList.contains('dm-kick')) {
        const payload: KickPlayerMessage = { seat: Number(t.dataset.seat) };
        this.room?.send(CLIENT_MESSAGES.KICK_PLAYER, payload);
      }
    });

    const chatInput = wrap.querySelector<HTMLInputElement>('#dm-chat-input')!;
    const chatSend = wrap.querySelector<HTMLButtonElement>('#dm-chat-send')!;
    let lastSentText = '';
    let lastSentAt = 0;
    const sendChat = (): void => {
      const text = chatInput.value.trim();
      if (!text) return;
      const now = performance.now();
      if (text === lastSentText && now - lastSentAt < 500) {
        chatInput.value = '';
        return;
      }
      lastSentText = text;
      lastSentAt = now;
      chatInput.value = '';
      const payload: ChatMessage = { text };
      this.room?.send(CLIENT_MESSAGES.CHAT, payload);
    };
    chatSend.addEventListener('click', sendChat);
    chatInput.addEventListener('keydown', (e) => {
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === 'Enter') sendChat();
    });
  }

  private render(): void {
    const s = this.state;
    if (!s || !this.overlay) return;
    const title = this.overlay.querySelector<HTMLDivElement>('#dm-room-title');
    const meta = this.overlay.querySelector<HTMLDivElement>('#dm-room-meta');
    if (title) title.textContent = s.roomName || '방';
    if (meta) {
      const opt = s.options;
      meta.textContent = `최대 ${opt.maxPlayers}명 · 상납 1등 ${opt.topTaxCount}장 · 혁명 ${opt.allowRevolution ? '허용' : '금지'} · 봇 ${opt.botThinkMs}ms`;
    }

    const grid = this.overlay.querySelector<HTMLDivElement>('#dm-seat-grid');
    if (grid) grid.innerHTML = this.renderSeatGrid(s);

    const isHost = s.hostSessionId === this.room?.sessionId;
    const hostBox = this.overlay.querySelector<HTMLDivElement>('#dm-host-controls');
    if (hostBox) hostBox.style.display = isHost ? 'block' : 'none';
    const playerBox = this.overlay.querySelector<HTMLDivElement>('#dm-player-controls');
    if (playerBox) playerBox.style.display = isHost ? 'none' : 'block';

    const me = s.seatOrder.find((p) => p.sessionId === this.room?.sessionId);
    const readyBtn = this.overlay.querySelector<HTMLButtonElement>('#dm-ready');
    if (readyBtn && me) {
      readyBtn.textContent = me.isReady ? '✓ 레디 (취소)' : '레디';
      readyBtn.style.background = me.isReady
        ? 'linear-gradient(180deg, #b8e8a8 0%, #7ec96a 100%)'
        : '';
    }

    const startBtn = this.overlay.querySelector<HTMLButtonElement>('#dm-start');
    if (startBtn) {
      const others = s.seatOrder.filter(
        (p) => !p.isBot && p.sessionId !== s.hostSessionId,
      );
      const allOthersReady = others.every((p) => p.isReady);
      const enoughPlayers = s.seatOrder.length >= 2;
      const ok = allOthersReady && enoughPlayers;
      startBtn.disabled = !ok;
      startBtn.style.opacity = ok ? '1' : '0.5';
      startBtn.style.cursor = ok ? 'pointer' : 'not-allowed';
    }
    const addBotBtn = this.overlay.querySelector<HTMLButtonElement>('#dm-add-bot');
    if (addBotBtn) {
      const full = s.seatOrder.length >= s.options.maxPlayers;
      addBotBtn.disabled = full;
      addBotBtn.style.opacity = full ? '0.5' : '1';
    }
  }

  private renderSeatGrid(s: ServerRoomStateMessage): string {
    const max = s.options.maxPlayers;
    const filled = s.seatOrder;
    const isHost = s.hostSessionId === this.room?.sessionId;
    const cells: string[] = [];
    for (let i = 0; i < max; i++) {
      const seat = filled[i];
      cells.push(this.renderSeatCell(seat, isHost, s.hostSessionId));
    }
    return cells.join('');
  }

  private renderSeatCell(
    seat: RoomSeatRow | undefined,
    viewerIsHost: boolean,
    hostSessionId: string,
  ): string {
    if (!seat) {
      return `
        <div style="background:rgba(255,255,255,0.03); border:1.5px dashed rgba(245,200,105,0.25); border-radius:10px; padding:20px 12px; text-align:center; color:rgba(244,236,214,0.4); font-size:12px; min-height:92px; display:flex; align-items:center; justify-content:center;">
          빈 자리
        </div>`;
    }
    const ready = seat.isReady;
    const accent = ready ? '#82dd9c' : 'rgba(245,200,105,0.3)';
    const bgGlow = ready
      ? 'background: linear-gradient(180deg, rgba(110,200,130,0.10) 0%, rgba(110,200,130,0.04) 100%);'
      : 'background: rgba(255,255,255,0.04);';
    const crown = seat.sessionId === hostSessionId ? '👑 ' : '';
    const tag = seat.isBot
      ? '<span style="font-size:9px; background:rgba(200,140,75,0.25); color:#ffb878; padding:1px 6px; border-radius:8px; margin-left:4px; border:1px solid rgba(200,140,75,0.4); font-weight:700; letter-spacing:0.5px;">BOT</span>'
      : '';
    const kickBtn =
      viewerIsHost && seat.sessionId !== hostSessionId
        ? seat.isBot
          ? `<button data-seat="${seat.seat}" class="dm-remove-bot lux-btn-danger" style="font-size:10px; padding:3px 8px; margin-top:6px; align-self:flex-start;">제거</button>`
          : `<button data-seat="${seat.seat}" class="dm-kick lux-btn-danger" style="font-size:10px; padding:3px 8px; margin-top:6px; align-self:flex-start;">추방</button>`
        : '';
    const initial = (seat.displayName || '?').slice(0, 1).toUpperCase();
    return `
      <div style="${bgGlow} border:1px solid ${accent}; border-radius:10px; padding:10px 12px; min-height:92px; display:flex; flex-direction:column; gap:6px; box-shadow: ${ready ? '0 0 12px rgba(130,221,156,0.15)' : 'none'};">
        <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:8px;">
          <div style="display:flex; align-items:center; gap:6px; min-width:0; flex:1;">
            <div style="width:22px; height:22px; border-radius:50%; background: linear-gradient(135deg, #c89b2f 0%, #8a5e1f 100%); display:flex; align-items:center; justify-content:center; font-weight:700; color:#fff; font-size:10px; flex-shrink:0;">${escapeHtml(initial)}</div>
            <div style="font-weight:700; color:#f4ecd6; font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${crown}${escapeHtml(seat.displayName)}${tag}</div>
          </div>
          <div style="color:${accent}; font-weight:900; font-size:14px; flex-shrink:0;">${ready ? '✓' : '·'}</div>
        </div>
        <div style="font-size:10px; color:rgba(244,236,214,0.45); letter-spacing:0.5px;">SEAT #${seat.seat + 1}</div>
        ${kickBtn}
      </div>`;
  }

  private renderChat(): void {
    const log = this.overlay?.querySelector<HTMLDivElement>('#dm-chat-log');
    if (!log) return;
    if (this.chat.length === 0) {
      log.innerHTML = `<div style="text-align:center; color:rgba(244,236,214,0.35); font-size:12px; margin:auto;">아직 채팅이 없습니다</div>`;
      return;
    }
    log.innerHTML = this.chat
      .map(
        (c) =>
          `<div style="color:#f4ecd6;"><span style="color:#f5c869; font-weight:700;">${escapeHtml(c.fromName)}</span> <span style="color:rgba(244,236,214,0.55);">:</span> <span>${escapeHtml(c.text)}</span></div>`,
      )
      .join('');
    log.scrollTop = log.scrollHeight;
  }

  private renderLog(): void {
    const log = this.overlay?.querySelector<HTMLDivElement>('#dm-log');
    if (!log) return;
    log.innerHTML = this.log
      .slice(-10)
      .map(
        (l) =>
          `<div style="color:rgba(244,236,214,0.7);">· ${escapeHtml(l.text)}</div>`,
      )
      .join('');
    log.scrollTop = log.scrollHeight;
  }

  private openOptionsModal(): void {
    if (!this.state) return;
    const root = document.getElementById('overlay');
    if (!root) return;
    const cur = this.state.options;
    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(10, 4, 6, 0.75);
      backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      pointer-events: auto; z-index: 200;
    `;
    const modal = document.createElement('div');
    modal.className = 'lux-panel';
    modal.style.cssText = `width: 560px; max-height: 80vh; overflow-y:auto; pointer-events:auto; padding: 22px;`;
    const optRow = (
      label: string,
      key: string,
      choices: [string, string][],
    ): string => {
      const buttons = choices
        .map(
          ([v, l]) =>
            `<button data-opt="${key}" data-val="${v}" class="dm-opt-${key} lux-btn-ghost" style="flex:1; padding:6px 6px; font-size:11px;">${l}</button>`,
        )
        .join('');
      return `
        <div style="display:flex; align-items:center; gap:10px;">
          <span style="min-width:110px; color:rgba(244,236,214,0.75); font-weight:600;">${label}</span>
          <div style="flex:1; display:flex; gap:4px;">${buttons}</div>
        </div>`;
    };
    modal.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <h3 style="margin:0;">⚙ 방 옵션 (방장 전용)</h3>
        <button id="dm-modal-close" class="lux-btn-ghost" style="padding:4px 10px; font-size:14px;">✕</button>
      </div>
      <div class="lux-divider"></div>
      <div style="font-size:11px; color:#ffb878; padding:8px 10px; background:rgba(220,140,80,0.08); border-left:2px solid rgba(220,140,80,0.4); border-radius:4px; margin-bottom:12px;">⚠ 옵션 변경 시 모든 플레이어의 레디가 해제됩니다.</div>
      <div style="display:flex; flex-direction:column; gap:10px; font-size:12px;">
        ${optRow('최대 인원', 'maxPlayers', [2, 3, 4, 5, 6, 7, 8].map((n) => [String(n), `${n}명`] as [string, string]))}
        ${optRow('순서 정렬', 'reorder', [['none', '재배치 없음'], ['byRank', '직위순 재배치']])}
        ${optRow('2등 상납 인원', 'secondPair', [['9', '항상 1등만'], ['5', '5명 이상'], ['4', '4명부터'], ['3', '3명부터']])}
        ${optRow('1등 상납 카드 수', 'topTax', [['1', '1장'], ['2', '2장'], ['3', '3장']])}
        ${optRow('혁명', 'revolution', [['true', '허용'], ['false', '금지']])}
        ${optRow('봇 속도', 'botSpeed', [['200', '빠름'], ['450', '보통'], ['800', '느림']])}
        ${optRow('첫 시작자', 'firstStart', [['random', '무작위'], ['firstJoiner', '첫 입장자']])}
        ${optRow('라운드 대기', 'roundDelay', [['0', '즉시'], ['3000', '3초'], ['5000', '5초']])}
      </div>
      <button id="dm-modal-done" class="lux-btn-primary" style="margin-top:18px; width:100%; padding:11px;">닫기</button>
    `;
    backdrop.appendChild(modal);
    root.appendChild(backdrop);

    const refresh = (): void => {
      const setSel = (cls: string, val: string): void => {
        modal.querySelectorAll<HTMLButtonElement>(`.${cls}`).forEach((b) => {
          const active = (b.dataset.val ?? '') === val;
          b.classList.toggle('lux-btn-primary', active);
          b.classList.toggle('lux-btn-ghost', !active);
        });
      };
      const opt = this.state?.options ?? cur;
      setSel('dm-opt-maxPlayers', String(opt.maxPlayers));
      setSel('dm-opt-reorder', opt.reorderByRank ? 'byRank' : 'none');
      setSel('dm-opt-secondPair', String(opt.secondPairFromSize));
      setSel('dm-opt-topTax', String(opt.topTaxCount));
      setSel('dm-opt-revolution', String(opt.allowRevolution));
      setSel('dm-opt-botSpeed', String(opt.botThinkMs));
      setSel('dm-opt-firstStart', opt.firstRoundStarter);
      setSel('dm-opt-roundDelay', String(opt.roundDelayMs));
    };
    modal.querySelectorAll<HTMLButtonElement>('[data-opt]').forEach((b) => {
      b.addEventListener('click', () => {
        const k = b.dataset.opt;
        const v = b.dataset.val ?? '';
        const patch: Partial<RoomOptions> = {};
        switch (k) {
          case 'maxPlayers':
            patch.maxPlayers = Number(v);
            break;
          case 'reorder':
            patch.reorderByRank = v === 'byRank';
            break;
          case 'secondPair':
            patch.secondPairFromSize = Number(v);
            break;
          case 'topTax':
            patch.topTaxCount = Number(v);
            break;
          case 'revolution':
            patch.allowRevolution = v === 'true';
            break;
          case 'botSpeed':
            patch.botThinkMs = Number(v);
            break;
          case 'firstStart':
            patch.firstRoundStarter = v as 'random' | 'firstJoiner';
            break;
          case 'roundDelay':
            patch.roundDelayMs = Number(v);
            break;
        }
        // Optimistic UI: reflect the click immediately, then let the
        // authoritative state from the server fix it up if it was rejected.
        if (this.state) {
          this.state = {
            ...this.state,
            options: { ...this.state.options, ...patch },
          };
          refresh();
        }
        const msg: UpdateRoomOptionsMessage = { options: patch };
        this.room?.send(CLIENT_MESSAGES.UPDATE_ROOM_OPTIONS, msg);
      });
    });
    refresh();
    // Re-render on each state update while modal is open.
    const reflow = (): void => refresh();
    this.events.on('room-state', reflow);

    const close = (): void => {
      this.events.off('room-state', reflow);
      backdrop.remove();
    };
    modal
      .querySelector<HTMLButtonElement>('#dm-modal-close')!
      .addEventListener('click', close);
    modal
      .querySelector<HTMLButtonElement>('#dm-modal-done')!
      .addEventListener('click', close);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
  }

  private copyInviteLink(): void {
    const id = this.room?.roomId;
    if (!id) return;
    const url = `${window.location.origin}${window.location.pathname}#room/${id}`;
    const done = (): void => this.setStatus('📋 초대 링크가 복사되었습니다');
    const fallback = (): void => {
      // Clipboard API may be unavailable (insecure context). Fall back to
      // showing the link so the user can copy manually.
      this.setStatus(`초대 링크: ${url}`);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(done)
        .catch(fallback);
    } else {
      fallback();
    }
  }

  private setStatus(text: string): void {
    const el = this.overlay?.querySelector<HTMLDivElement>('#dm-status');
    if (el) {
      el.textContent = text;
      setTimeout(() => {
        if (el.textContent === text) el.textContent = '';
      }, 4000);
    }
  }

  private unmountUI(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
